// @flow
import { array, boolean, number, object, string } from 'yup';
import { delay } from '@src/common-utils/utils';
import { FLINK_POOL_CFU_MAX_LIMIT } from '@src/constants';

import { addMonthIndexToInputs, DELAY_BETWEEN_MUTATIONS_MS, throwOnError } from './utils';

export const createFlinkPoolSchema = (numberOfMonths, metaData) =>
  object({
    name: string().required('name is required'),
    provider: string().required('provider is required'),
    region: string().required('region is required'),
    inputs: array()
      .of(
        object({
          is_enabled: boolean().required('is_enabled is required'),
          cfu_count: number()
            .min(0)
            .max(metaData?.flink_pool_config?.cfu_count_threshold ?? FLINK_POOL_CFU_MAX_LIMIT)
            .required('cfu_count is required'),
        })
          .noUnknown(true)
          .strict()
      )
      .length(numberOfMonths, `flink inputs must have exactly ${numberOfMonths} entries`)
      .required(),
  })
    .noUnknown(true)
    .strict();

export const createFlinkPoolsFromBaseline = async (
  flinkPools,
  estimateId,
  currentVersion,
  addFlinkPool,
  updateFlinkPool
) => {
  let version = currentVersion;

  for (const pool of flinkPools) {
    const { error: createError, data: createData } = await addFlinkPool({
      estimateId,
      payload: {
        estimate_id: estimateId,
        name: pool.name,
        estimate_version: version,
      },
    });

    throwOnError(createError, `creating flink pool "${pool.name}"`);

    const flinkPoolId = createData.id;
    version = createData.estimate_version;

    const updatePayload = {
      id: flinkPoolId,
      estimate_id: estimateId,
      estimate_version: version,
      name: pool.name,
      inputs: {
        provider: pool.provider,
        region: pool.region,
      },
      flink: { inputs: addMonthIndexToInputs(pool.inputs) },
    };

    const { error: updateError, data: updateData } = await updateFlinkPool({
      estimateId,
      flinkPoolId,
      payload: updatePayload,
    });

    throwOnError(
      updateError,
      `updating flink pool "${pool.name}" with inputs:\n\n${JSON.stringify(updatePayload, null, 2)}`
    );

    version = updateData.estimate_version;
    await delay(DELAY_BETWEEN_MUTATIONS_MS);
  }

  return version;
};
